import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getToken } from '../utils/auth';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const GeneralLedgerReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [reportData, setReportData] = useState(null);
  const [expandedAccounts, setExpandedAccounts] = useState(new Set());

  useEffect(() => {
    fetchAccountingPeriods();
  }, []);

  useEffect(() => {
    if (selectedPeriod) {
      fetchReportData(selectedPeriod);
    } else {
      setReportData(null);
    }
  }, [selectedPeriod]);

  const fetchAccountingPeriods = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${baseURL}/api/accounting-periods`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const sortedPeriods = response.data.sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
      });
      setAccountingPeriods(sortedPeriods);
      
      // Set default to most recent open period
      const openPeriods = sortedPeriods.filter(p => p.status === 'open');
      if (openPeriods.length > 0) {
        setSelectedPeriod(openPeriods[0].vuid);
      }
    } catch (error) {
      console.error('Error fetching accounting periods:', error);
      setError('Failed to fetch accounting periods');
    }
  };

  const fetchReportData = async (periodVuid) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getToken();
      const response = await axios.get(`${baseURL}/api/general-ledger-report`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { accounting_period_vuid: periodVuid }
      });
      
      setReportData(response.data);
      setExpandedAccounts(new Set()); // Reset expanded accounts when data changes
    } catch (error) {
      console.error('Error fetching general ledger report:', error);
      setError(error.response?.data?.error || 'Failed to fetch general ledger report');
    } finally {
      setLoading(false);
    }
  };

  const toggleAccount = (accountVuid) => {
    setExpandedAccounts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountVuid)) {
        newSet.delete(accountVuid);
      } else {
        newSet.add(accountVuid);
      }
      return newSet;
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const getTransactionTypeLabel = (type) => {
    const labels = {
      'ap_invoice': 'AP Invoice',
      'project_billing': 'Project Billing',
      'labor_cost': 'Labor Cost',
      'project_expense': 'Project Expense',
      'ap_invoice_payment': 'AP Invoice Payment',
      'project_billing_payment': 'Project Billing Payment'
    };
    return labels[type] || type;
  };

  // CSV/Excel Export functionality
  const downloadCsvFile = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleExcelExport = () => {
    if (!reportData) return;

    const headers = [
      'Account Number',
      'Account Name',
      'Transaction Type',
      'Reference Number',
      'Description',
      'Project Number',
      'Project Name',
      'Posted Date',
      'Debit Amount',
      'Credit Amount',
      'External IDs'
    ];

    const rows = [];
    reportData.accounts.forEach(account => {
      account.transactions.forEach(transaction => {
        const externalIdsStr = transaction.external_ids && transaction.external_ids.length > 0
          ? transaction.external_ids.map(extId => `${extId.integration_name}: ${extId.external_id}`).join('; ')
          : '';
        
        rows.push([
          account.account_number || '',
          account.account_name || '',
          getTransactionTypeLabel(transaction.transaction_type),
          transaction.reference_number || '',
          transaction.description || '',
          transaction.project?.project_number || '',
          transaction.project?.project_name || '',
          formatDate(transaction.posted_at),
          transaction.debit_amount > 0 ? transaction.debit_amount.toFixed(2) : '',
          transaction.credit_amount > 0 ? transaction.credit_amount.toFixed(2) : '',
          externalIdsStr
        ]);
      });
    });

    // Add totals row - must match header order exactly (11 columns: 0-10)
    if (reportData.summary) {
      rows.push([
        '',                    // 0: Account Number
        '',                    // 1: Account Name
        'TOTAL',               // 2: Transaction Type
        '',                    // 3: Reference Number
        '',                    // 4: Description
        '',                    // 5: Project Number
        '',                    // 6: Project Name
        '',                    // 7: Posted Date
        (reportData.summary.total_debits || 0).toFixed(2),    // 8: Debit Amount
        (reportData.summary.total_credits || 0).toFixed(2),   // 9: Credit Amount
        ''                     // 10: External IDs
      ]);
    }

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => {
        const fieldStr = String(field || '').replace(/"/g, '""');
        return `"${fieldStr}"`;
      }).join(','))
      .join('\n');

    const period = reportData.accounting_period;
    const filename = `general_ledger_${period.month}_${period.year}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCsvFile(csvContent, filename);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-container, .print-container * {
            visibility: visible;
          }
          .print-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          table {
            border-collapse: collapse;
            width: 100%;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          th {
            background-color: #f2f2f2;
            font-weight: bold;
          }
        }
      `}</style>
      <div className="container mx-auto px-4 py-8 print-container">
      <div className="mb-8 no-print">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">General Ledger Report</h1>
        <p className="text-gray-600">
          View all GL account transactions for a selected accounting period
        </p>
      </div>

      {/* Accounting Period Selector */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6 no-print">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Accounting Period
            </label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select an accounting period...</option>
              {accountingPeriods.map(period => (
                <option key={period.vuid} value={period.vuid}>
                  {period.month}/{period.year} - {period.description || 'N/A'} ({period.status})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6 no-print">
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading general ledger report...</div>
        </div>
      )}

      {/* Report Data */}
      {!loading && reportData && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Period Header with Export Buttons */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              {reportData.accounting_period.month}/{reportData.accounting_period.year} - {reportData.accounting_period.description || 'N/A'}
            </h2>
            <div className="flex space-x-2 print:hidden">
              <button
                onClick={handleExcelExport}
                disabled={!reportData || reportData.accounts.length === 0}
                className="btn-claret disabled:opacity-50 disabled:cursor-not-allowed"
                title="Export to Excel (CSV)"
              >
                <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Export to Excel</span>
              </button>
              <button
                onClick={handlePrint}
                disabled={!reportData || reportData.accounts.length === 0}
                className="btn-claret disabled:opacity-50 disabled:cursor-not-allowed"
                title="Print to PDF or Printer"
              >
                <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                <span>Print</span>
              </button>
            </div>
          </div>

          {/* Accounts Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    {/* Expand/collapse column */}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account Name
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Debits
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Credits
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.accounts.map((account) => {
                  const isExpanded = expandedAccounts.has(account.account_vuid);
                  return (
                    <React.Fragment key={account.account_vuid}>
                      {/* Account Summary Row */}
                      <tr 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => toggleAccount(account.account_vuid)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          {account.transactions.length > 0 && (
                            <button className="text-gray-400 hover:text-gray-600">
                              {isExpanded ? (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              )}
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {account.account_number || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {account.account_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                          {account.total_debits > 0 ? formatCurrency(account.total_debits) : ''}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                          {account.total_credits > 0 ? formatCurrency(account.total_credits) : ''}
                        </td>
                      </tr>
                      
                      {/* Expanded Transaction Details */}
                      {isExpanded && account.transactions.map((transaction) => (
                        <tr key={transaction.line_item_vuid} className="bg-gray-50">
                          <td></td>
                          <td className="px-6 py-2 text-xs text-gray-500" colSpan="2">
                            <div className="pl-4 border-l-2 border-gray-300">
                              <div className="font-medium text-gray-700">
                                {getTransactionTypeLabel(transaction.transaction_type)} - {transaction.reference_number}
                              </div>
                              <div className="text-gray-500 mt-1">
                                {transaction.description || 'No description'}
                              </div>
                              {transaction.project && (
                                <div className="text-gray-500 mt-1">
                                  Project: {transaction.project.project_number} - {transaction.project.project_name}
                                </div>
                              )}
                              <div className="text-gray-400 mt-1">
                                {formatDate(transaction.posted_at)}
                              </div>
                              {transaction.external_ids && transaction.external_ids.length > 0 && (
                                <div className="text-gray-600 mt-2 pt-2 border-t border-gray-200">
                                  <div className="font-medium text-gray-700 mb-1">External IDs:</div>
                                  {transaction.external_ids.map((extId, idx) => (
                                    <div key={idx} className="text-xs mt-1">
                                      <span className="font-medium">{extId.integration_name}</span>: {extId.external_id}
                                      {extId.external_object_type && extId.external_object_type !== extId.integration_type && (
                                        <span className="text-gray-400"> ({extId.external_object_type})</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-xs text-gray-600 text-right">
                            {transaction.debit_amount > 0 ? formatCurrency(transaction.debit_amount) : ''}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-xs text-gray-600 text-right">
                            {transaction.credit_amount > 0 ? formatCurrency(transaction.credit_amount) : ''}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                
                {/* Totals Row */}
                {reportData.summary && (
                  <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                    <td className="px-6 py-4" colSpan="3">
                      <div className="text-sm font-semibold text-gray-900">TOTAL</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(reportData.summary.total_debits)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(reportData.summary.total_credits)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Balance Check */}
          {reportData.summary && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <div className="text-sm font-medium text-gray-700">
                  Balance Check:
                </div>
                <div className={`text-sm font-semibold ${
                  Math.abs(reportData.summary.balance) < 0.01 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {Math.abs(reportData.summary.balance) < 0.01 
                    ? '✓ Balanced (Debits = Credits)'
                    : `⚠ Out of Balance: ${formatCurrency(Math.abs(reportData.summary.balance))} difference`
                  }
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* No Data Message */}
      {!loading && selectedPeriod && reportData && reportData.accounts.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-gray-500">No transactions found for the selected accounting period.</p>
        </div>
      )}
      </div>
    </>
  );
};

export default GeneralLedgerReport;



